import { UserType } from "@/models/enums";
import { storageService } from "@/services/storage.service";
import { useUserStore } from "@/store/user";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import useBook from "./useBook";
import useBookingValidation from "./useBookingValidation";
import usePassengers from "./usePassengers";

const loadingNextStep = ref(false);
const routeGuardAdded = ref(false);

const useBookingNavigation = () => {
    const { stepIsValid } = useBookingValidation();
    const { book, confirm } = useBook();
    const { setPassengers } = usePassengers();
    const userStore = useUserStore();

    const defaultSteps = [
        {
            number: 1,
            name: "Book"
        },
        {
            number: 2,
            name: "Departures"
        },
        {
            number: 3,
            name: "Extras"
        },
        {
            number: 4,
            name: "Passenger details"
        },
        {
            number: 5,
            name: "Checkout"
        },
        {
            number: 6,
            name: "Payment"
        }
    ]

    const steps = computed(() => {
        if (userStore.getUserType === UserType.Agent) {
            return defaultSteps.slice(0, 5);
        }
        return defaultSteps;
    })

    const actionSteps = [
        {
            from: 2,
            to: 3,
            name: "Departures",
            actions: [book]
        },
        {
            from: 3,
            to: 4,
            name: "Extras",
            actions: [book]
        },
        {
            from: 4,
            to: 5,
            name: "Passenger details",
            actions: [setPassengers]
        },
        {
            from: 5,
            to: 6,
            name: "Checkout",
            actions: [confirm]
        },
        {
            from: 4,
            to: 6,
            name: "AgentCheckout",
            actions: [confirm]
        },
    ]

    const router = useRouter();

    const getActions = (toStep: number, fromStep: number) => {
        const actions: (() => Promise<any>)[] = [];
        const actionStep = actionSteps.find(bs => bs.to === toStep && bs.from === fromStep);
        if (actionStep) {
            actions.push(...actionStep.actions);
        }
        return actions;
    }

    const resolveNavigation = (toStep: number, fromStep: number, next: any, redirectToStep: number | undefined = undefined) => {
        Promise.all(getActions(toStep, fromStep).map(a => a())).then(() => {
            if (redirectToStep) {
                next({
                    query: {
                        step: redirectToStep
                    }
                })
            } else {
                next();
            }
        }).finally(() => {
            loadingNextStep.value = false;
        });
    }

    if (!routeGuardAdded.value) {
        routeGuardAdded.value = true;
        router.beforeEach((to, from, next) => {
            if (to.path !== from.path) return next();
            loadingNextStep.value = true;
            const fromStep = +(from.query.step as string ?? '1');
            const toStep = +(to.query.step as string ?? '1');
            //If we are stepping backwards, or the fromStep is valid and we're going to the next step
            if (toStep <= fromStep || stepIsValid(fromStep) && fromStep + 1 === toStep || userStore.getUserType === UserType.Agent) {
                resolveNavigation(toStep, fromStep, next);
            } else {
                const completedSteps = steps.value.filter(s => stepIsValid(s.number));
                if (currentStep.value.number > completedSteps.length) {
                    const step = completedSteps.length + 1;
                    next({
                        query: {
                            step
                        }
                    });
                }

                loadingNextStep.value = false;
            }
        });
    }

    const currentStep = computed(() => {
        return steps.value.find(s => s.number === parseInt(router.currentRoute.value.query.step?.toString() ?? "1")) ?? steps.value[0];
    })

    const currentStepIsValid = computed(() => {
        return stepIsValid(currentStep.value.number);
    })

    const nextStep = () => {
        if (currentStepIsValid.value) {
            goToStep(currentStep.value.number + 1);
        }
    }

    const prevStep = () => {
        goToStep((currentStep.value.number - 1) || 1);
    }

    const completedSteps = computed(() => {
        return steps.value.filter(s => s.number < + (route.query.step ?? 1));
    });

    const route = useRoute();

    const goToStep = (step: number) => {
        router.push({
            path: router.currentRoute.value.path, query: {
                step
            }
        });
    }

    const redirectToAllowedStep = () => {
        //If landing on a step not completed, redirect to the last valid step + 1
        if (currentStep.value.number > 1) {
            const completedSteps = steps.value.filter(s => stepIsValid(s.number));
            if (currentStep.value.number > completedSteps.length + 1) {
                goToStep(completedSteps.length + 1);
            }
        }
    }

    onMounted(() => {
        redirectToAllowedStep();
    });

    const cancel = () => {
        storageService.clearAll();
        window.location.replace(location.pathname);
    }

    return { currentStep, currentStepIsValid, goToStep, steps, nextStep, prevStep, completedSteps, loadingNextStep, cancel }
}

export default useBookingNavigation;